import { useEffect, useState, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import axios from "axios";
import { X } from "lucide-react";
import confetti from "canvas-confetti";
import { BadgePercent, Gift, Sparkles, Snowflake } from "lucide-react";
import { getStaticPricing } from '../../utils/pricingConfig';
import { PaymentGatewayRef } from './PaymentGateway';
import { useNavigate } from 'react-router-dom'
import useCredits from '../../hooks/useCredits'

interface OfferModalProps {
  open: boolean;
  onClose: () => void;
  handleopenPricingModal:()=>void;
  paymentGatewayRef?: React.RefObject<PaymentGatewayRef | null>;
  documentId?: string | null;
  userPlan?: string;
  exportPaid?: boolean;
}

export default function OfferModal({ 
  open, 
  onClose,
  handleopenPricingModal,
  paymentGatewayRef,
  documentId,
  userPlan,
  exportPaid
}: OfferModalProps) {
  const [currency, setCurrency] = useState("INR"); // Default currency is INR
  const [price, setPrice] = useState({ current: 199, original: 99 });
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Utility function to check if document ID has odd last digit
  const isDocumentIdOdd = (id: string | null | undefined): boolean => {
    if (!id) return false;
    const lastDigit = parseInt(id.charAt(id.length - 1));
    return !isNaN(lastDigit) && lastDigit % 2 === 1;
  };

  // Calculate time remaining until end of day
  const calculateTimeLeft = useCallback(() => {
    const now = new Date();
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    
    const difference = endOfDay.getTime() - now.getTime();
    
    if (difference > 0) {
      setTimeLeft({
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      });
    } else {
      // If day has ended, reset to 24 hours
      setTimeLeft({
        hours: 24,
        minutes: 0,
        seconds: 0,
      });
    }
  }, []);

  // Handle export button click
  const handleExportClick = () => {
    // New flow: use credits system and central pricing route
    const navigate = navigateRef.current
    // conservative cost: use original price mapping? here use document full generation cost (10 credits)
    const requiredCredits = 10

    // If user has enough credits, navigate to pricing with prefill to continue (or proceed to generation in real flow)
    if (creditsAvailable !== null && creditsAvailable >= requiredCredits) {
      // user has enough credits; we'll route to pricing page which will handle the unified payment/credits flow
      navigate(`/pricing?from=offer&cost=${requiredCredits}`)
      onClose()
      return
    }

    // Not enough credits -> go to central pricing/top-up flow
    navigate(`/pricing?from=offer&cost=${requiredCredits}&insufficient=true`)
    onClose()
  };

  // navigation and credits
  const navigate = useNavigate()
  const navigateRef = { current: navigate }
  const { credits: creditsAvailable, refresh, ensureCredits } = useCredits()

  useEffect(() => {
    const fetchCurrency = async () => {
      try {
        const response = await axios.get("https://api.zynth.ai/api/v1/data/userprofile/ip-info");
        const userCountry = response.data?.country;
        const resolvedCurrency = userCountry !== "IN" ? "USD" : "INR";

        setCurrency(resolvedCurrency);
        const pricing = getStaticPricing(resolvedCurrency);
        setPrice({ current: pricing.current, original: pricing.original });
      } catch (error) {
        console.error("Failed to fetch IP info:", error);
      }
    };

    fetchCurrency();
  }, []);

  // Timer effect - updates every second
  useEffect(() => {
    calculateTimeLeft(); // Calculate immediately when modal opens
    
    const timer = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(timer);
  }, [open, calculateTimeLeft]);

  useEffect(() => {
    if (open) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={() => {}} modal={true}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" style={{ zIndex: 9998 }} />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                     bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50
                     border-4 border-blue-500
                     p-4 sm:p-6 rounded-2xl shadow-2xl 
                     w-[90%] max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl 
                     text-center relative overflow-hidden z-50"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999
          }}
        >
          {/* Decorative snowflakes */}
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden opacity-20">
            <Snowflake className="absolute top-4 left-4 text-blue-400 w-8 h-8 animate-spin" style={{ animationDuration: '10s' }} />
            <Snowflake className="absolute top-8 right-8 text-blue-300 w-6 h-6 animate-spin" style={{ animationDuration: '15s' }} />
            <Snowflake className="absolute bottom-12 left-12 text-blue-400 w-10 h-10 animate-spin" style={{ animationDuration: '12s' }} />
            <Snowflake className="absolute bottom-8 right-16 text-blue-300 w-7 h-7 animate-spin" style={{ animationDuration: '8s' }} />
          </div>

          <Dialog.Close className="absolute top-3 right-3 z-10 text-gray-600 hover:text-blue-600 transition-colors" onClick={onClose}>
            <X size={24} />
          </Dialog.Close>

          {/* Winter Holiday Banner */}
          <div className="mb-4 relative z-10">
            <div className="inline-block bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2 rounded-full font-bold text-sm tracking-wider shadow-lg">
              ❄️ WINTER HOLIDAY SPECIAL ❄️
            </div>
          </div>

          <Dialog.Title
            className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 
                       animate-bounce flex items-center justify-center gap-2 relative z-10"
            style={{ animation: "bounce 1s ease infinite", animationIterationCount: 4 }}
          >
            <Snowflake className="text-blue-600 w-5 sm:w-6 h-5 sm:h-6 drop-shadow-lg" />
            <span>50% OFF</span>
            <Sparkles className="text-cyan-600 w-5 sm:w-6 h-5 sm:h-6 drop-shadow-lg" />
          </Dialog.Title>

          <p className="text-base sm:text-lg mt-2 font-semibold text-gray-700 relative z-10">
            ❄️ Exclusive Winter Holiday Sale!
          </p>

          <p className="text-base sm:text-lg mt-4 text-gray-700 relative z-10">
            <span className="text-lg sm:text-xl text-gray-500 line-through">
              {currency} - {price.current}
            </span>
            <span className="text-xl sm:text-2xl font-bold text-blue-700 ml-3 sm:ml-4">
              {currency} - {price.original}
            </span>
          </p>
          
          <p className="text-base sm:text-lg mt-3 sm:mt-4 relative z-10">
            ❄️ Export to Google Slides for complete presentation control.
          </p>

          <ul className="text-left text-gray-700 mt-4 space-y-3 sm:space-y-4 relative z-10">
            {[
              "Edit and format text seamlessly.",
              "Add or replace graphics and images as needed.",
              "Complete sync between Zynth and Google Slides.",
            ].map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <svg
                  className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <button
            className="mt-5 sm:mt-6 px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-base sm:text-lg font-semibold rounded-full hover:from-blue-700 hover:to-cyan-700 transition transform hover:scale-105 shadow-xl relative z-10 border-2 border-white"
            onClick={handleExportClick}
          >
            ❄️ Get Winter Holiday Deal - Save 50% ❄️
          </button>

          {/* Countdown Timer */}
          <div className="mt-4 sm:mt-6 relative z-10">
            <p className="text-sm text-gray-600 mb-2">⏰ Offer Valid Until:</p>
            <div className="bg-gradient-to-r from-blue-100 to-cyan-100 rounded-lg p-3 border border-blue-200">
              <div className="flex justify-center items-center space-x-4 text-lg font-bold text-blue-700">
                <div className="text-center">
                  <div className="text-2xl">{timeLeft.hours.toString().padStart(2, '0')}</div>
                  <div className="text-xs text-gray-600">Hours</div>
                </div>
                <div className="text-blue-500">:</div>
                <div className="text-center">
                  <div className="text-2xl">{timeLeft.minutes.toString().padStart(2, '0')}</div>
                  <div className="text-xs text-gray-600">Minutes</div>
                </div>
                <div className="text-blue-500">:</div>
                <div className="text-center">
                  <div className="text-2xl">{timeLeft.seconds.toString().padStart(2, '0')}</div>
                  <div className="text-xs text-gray-600">Seconds</div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 mt-3 relative z-10">
            ⏰ Limited time offer - Ends soon!
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
